import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { FloatLabelType } from '@angular/material/form-field';
import { CdwSelectOption } from '@cdw-ae/shared-interfaces';
import { map, Observable, Subject, startWith, take } from 'rxjs';

@Component({
  selector: 'lib-cdw-autocomplete',
  templateUrl: './cdw-autocomplete.component.html',
  styleUrls: ['./cdw-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CdwAutocompleteComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CdwAutocompleteComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
  @Input() label = '';

  @Input() placeholder = '';

  @Input() disabled = false;

  @Input() values: CdwSelectOption[] | Observable<CdwSelectOption[]> = [];

  @Input() multiSelect = false;

  @Input() showInternalSearch = false;

  @Input() hideMainInput = false;

  @Input() floatLabel!: FloatLabelType;

  @Input() hideRequiredMarker = false;

  @Input() dropdownHeight = 256;

  @Input() fontSize?: string;

  @Input() fontWeight?: string | number;

  @Input() fontColor?: string;

  @Input() fontFamily?: string;

  @Input() disableLastPass = true; // Disable LastPass autocomplete by default

  @Output() doneClicked = new EventEmitter<CdwSelectOption[]>();

  @Output() textChanged = new EventEmitter<string>();

  @ViewChild('triggerInput', { read: MatAutocompleteTrigger })
  autocompleteTrigger!: MatAutocompleteTrigger;

  currentValue: CdwSelectOption | null = null;

  selectedItems: CdwSelectOption[] = [];

  searchText = '';

  filteredOptions!: Observable<CdwSelectOption[]>;

  private searchInput$ = new Subject<string>();

  // Form control handlers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // biome-ignore lint/suspicious/noEmptyBlockStatements: ControlValueAccessor interface requires these methods, implemented by registerOnChange/registerOnTouched
  onChange = (value: unknown) => {};

  // biome-ignore lint/suspicious/noEmptyBlockStatements: ControlValueAccessor interface requires these methods, implemented by registerOnChange/registerOnTouched
  onTouched = () => {};

  private onScrollCapture = (event: Event) => {
    if (!this.autocompleteTrigger?.panelOpen) return;

    // Don't close when scrolling within the autocomplete dropdown itself
    const target = event.target;
    if (
      target instanceof HTMLElement &&
      target.closest('.cdk-overlay-container')
    )
      return;

    // Close panel on page scroll to prevent header overlap (EDUS-2259)
    this.ngZone.run(() => this.autocompleteTrigger.closePanel());
  };

  constructor(
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.setupFilteredOptions();
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('scroll', this.onScrollCapture, true);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScrollCapture, true);
  }

  // ==================== ControlValueAccessor ====================
  writeValue(value: unknown): void {
    if (this.multiSelect) {
      // Normalize scalar → single-element array so a parent form initialized
      // with `roles: 5` still pre-checks role 5 in the dropdown. Without this,
      // the user opens the picker, sees nothing checked, and unwittingly wipes
      // the original selection by picking new options.
      const valueArray: unknown[] = Array.isArray(value)
        ? value
        : value != null
          ? [value]
          : [];
      this.selectedItems = [];
      this.matchValuesToOptions(valueArray);
    } else {
      this.currentValue = value as CdwSelectOption;
    }
  }

  private matchValuesToOptions(valueArray: unknown[]): void {
    // If values is an Observable, subscribe to get the options
    if (this.values && 'subscribe' in this.values) {
      this.values
        .pipe(
          take(1),
          map((options) => options || [])
        )
        .subscribe((allOptions) => {
          this.selectedItems = [];
          valueArray.forEach((val) => {
            const matchingOption = allOptions.find((opt) => opt?.value === val);
            if (matchingOption) {
              this.selectedItems.push(matchingOption);
            }
          });
          this.changeDetector.markForCheck();
        });
    } else {
      const allOptions = Array.isArray(this.values) ? this.values : [];
      valueArray.forEach((val) => {
        const matchingOption = allOptions.find((opt) => opt?.value === val);
        if (matchingOption) {
          this.selectedItems.push(matchingOption);
        }
      });
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ==================== User Actions ====================
  handleInputTyping(event: Event): void {
    if (this.showInternalSearch || this.multiSelect) {
      return;
    }
    const text = (event.target as HTMLInputElement).value || '';
    this.searchInput$.next(text);
    this.textChanged.emit(text);
  }

  handleSingleSelection(event: MatAutocompleteSelectedEvent): void {
    if (this.multiSelect) {
      return;
    }
    const selected = event.option.value as CdwSelectOption;
    this.currentValue = selected;
    this.onChange(selected.value);
    this.onTouched();
  }

  handleSearchFieldChange(searchText: string): void {
    this.searchText = searchText;
    this.searchInput$.next(searchText);
  }

  openDropdown(): void {
    if ((this.showInternalSearch || this.multiSelect) && !this.disabled) {
      this.autocompleteTrigger?.openPanel();
    }
  }

  // ==================== Multiple Selection ====================
  handleOptionClick(option: CdwSelectOption, event: Event): void {
    if (this.multiSelect && option) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isItemSelected(option)) {
        this.selectedItems = this.selectedItems.filter(
          (item) => item?.value !== option.value
        );
      } else {
        this.selectedItems = [...this.selectedItems, option];
      }

      // Notify form of changes
      const values = this.selectedItems
        .filter((item) => item?.value)
        .map((item) => item.value);
      this.onChange(values);
      this.changeDetector.markForCheck();
    }
  }

  finishMultipleSelection(): void {
    this.doneClicked.emit(this.selectedItems);
    this.autocompleteTrigger?.closePanel();
  }

  // eslint-disable-next-line class-methods-use-this
  handleActionClick(option: CdwSelectOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (option.showDetailsAction?.handler) {
      option.showDetailsAction.handler(option);
    }
  }

  isItemSelected(item: CdwSelectOption): boolean {
    if (!item?.value) return false;

    // In single-select mode, check against currentValue
    if (!this.multiSelect) {
      return this.currentValue?.value === item.value;
    }

    // In multi-select mode, check against selectedItems array
    return this.selectedItems.some(
      (selected) => selected?.value === item.value
    );
  }

  // ==================== Display Helpers ====================
  getDisplayText = (option: CdwSelectOption): string =>
    option?.displayName || '';

  getInputDisplayValue(): string {
    if (this.multiSelect) {
      return '';
    }
    return this.currentValue?.displayName || '';
  }

  // ==================== Filtering ====================
  private setupFilteredOptions(): void {
    if (Array.isArray(this.values)) {
      this.filteredOptions = this.searchInput$.pipe(
        startWith(''),
        map((searchText) =>
          this.filterOptions(searchText || '', this.values as CdwSelectOption[])
        )
      );
    } else {
      // If values is an Observable, use it directly
      this.filteredOptions = this.values;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private filterOptions(
    searchText: string,
    options: CdwSelectOption[]
  ): CdwSelectOption[] {
    if (!options) {
      return [];
    }

    if (!searchText || searchText.trim() === '') {
      return options;
    }

    const searchLower = searchText.toLowerCase();
    return options.filter((option) =>
      option?.displayName?.toLowerCase().includes(searchLower)
    );
  }
}
